import { useState, useEffect, useCallback } from "react";

const INDICATORS = [
  { key: "cape", label: "Shiller CAPE Ratio", description: "Cyclically adjusted P/E. Long-term avg ≈ 17. Near dot-com highs.", thresholds: [25, 32, 38], unit: "x", riskLabels: ["Normal", "Elevated", "High", "Extreme"], invert: false },
  { key: "buffett", label: "Buffett Indicator", description: "Market cap / GDP. All-time high territory. >200% = 'playing with fire'.", thresholds: [100, 150, 200], unit: "%", riskLabels: ["Undervalued", "Fair", "Overvalued", "Extreme"], invert: false },
  { key: "recession", label: "NY Fed Recession Prob.", description: "12-month recession probability from yield curve model.", thresholds: [15, 30, 50], unit: "%", riskLabels: ["Low", "Moderate", "Elevated", "High"], invert: false },
  { key: "unemployment", label: "US Unemployment", description: "Rising unemployment signals economic deterioration.", thresholds: [4.5, 5.5, 7.0], unit: "%", riskLabels: ["Healthy", "Watch", "Stress", "Crisis"], invert: false },
  { key: "vix", label: "VIX (Fear Index)", description: "Market volatility index. >30 = elevated fear.", thresholds: [20, 28, 40], unit: "", riskLabels: ["Calm", "Cautious", "Fear", "Panic"], invert: false },
  { key: "sp500change", label: "S&P 500 YTD Change", description: "Year-to-date performance of S&P 500.", thresholds: [-5, -10, -20], unit: "%", riskLabels: ["Positive", "Dip", "Correction", "Crash"], invert: true },
];

const DEFAULT_DATA = {
  cape: { value: null, source: "Shiller/Yale", updated: "Loading…" },
  buffett: { value: 221, source: "currentmarketvaluation.com", updated: "Apr 2026" },
  recession: { value: null, source: "NY Fed", updated: "Loading…" },
  unemployment: { value: null, source: "BLS", updated: "Loading…" },
  vix: { value: null, source: "CBOE", updated: "Loading…" },
  sp500change: { value: null, source: "Alpha Vantage", updated: "Loading…" },
};

const PUBLISHED_BASELINE = 26;

function getRiskLevel(key, value, thresholds, invert) {
  if (value === null) return 1;
  if (!invert) {
    if (value >= thresholds[2]) return 3;
    if (value >= thresholds[1]) return 2;
    if (value >= thresholds[0]) return 1;
    return 0;
  } else {
    if (value <= thresholds[2]) return 3;
    if (value <= thresholds[1]) return 2;
    if (value <= thresholds[0]) return 1;
    return 0;
  }
}

function getBarPct(key, value, thresholds, invert) {
  if (value === null) return 30;
  const max = thresholds[2] * 1.3;
  if (!invert) return Math.min(98, Math.max(4, (value / max) * 100));
  const absVal = Math.abs(value);
  const absMax = Math.abs(thresholds[2]) * 1.5;
  return Math.min(98, Math.max(4, (absVal / absMax) * 100));
}

const RISK_COLORS = ["#22c55e", "#eab308", "#f97316", "#ef4444"];
const RISK_BG = ["#052e16", "#422006", "#431407", "#450a0a"];
const NEWS_RISK_LABELS = ["Low", "Moderate", "Elevated", "Severe"];
const NEWS_RISK_COLORS = ["#22c55e", "#eab308", "#f97316", "#ef4444"];

function computeCrashProb(data) {
  let score = 0;
  let count = 0;
  INDICATORS.forEach(({ key, thresholds, invert }) => {
    const v = data[key]?.value;
    if (v === null || v === undefined) return;
    const level = getRiskLevel(key, v, thresholds, invert);
    score += level;
    count++;
  });
  if (count === 0) return PUBLISHED_BASELINE;
  const avg = score / count;
  const modelScore = Math.round(10 + avg * 12);
  return Math.round((PUBLISHED_BASELINE + modelScore) / 2);
}

function needleCoords(pct) {
  const angleDeg = 180 + (pct / 100) * 180;
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: 110 + 80 * Math.cos(angleRad), y: 110 + 80 * Math.sin(angleRad) };
}

export default function CrashDashboard() {
  const [liveData, setLiveData] = useState(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [newsRiskScore, setNewsRiskScore] = useState(null);
  const [newsRiskSummary, setNewsRiskSummary] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("indicators");
  const [apiError, setApiError] = useState("");

  const fetchLiveData = useCallback(async () => {
    setLoading(true);
    setApiError("");
    try {
      const res = await fetch("/api/market-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // Merge fetched data with defaults
      const updated = { ...DEFAULT_DATA };
      Object.keys(data).forEach(key => {
        if (data[key]) updated[key] = { ...updated[key], ...data[key] };
      });
      setLiveData(updated);
    } catch (e) {
      setApiError("Market data unavailable: " + e.message);
    }
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  const fetchNewsAndRisk = useCallback(async () => {
    setNewsLoading(true);
    try {
      const res = await fetch("/api/news", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setNewsItems(data.headlines || []);
      setNewsRiskScore(data.overallRisk ?? 1);
      setNewsRiskSummary(data.summary || "");
    } catch (e) {
      setNewsItems([]);
      setNewsRiskSummary("News feed unavailable.");
      setNewsRiskScore(1);
    }
    setNewsLoading(false);
  }, []);

  const fetchAiAnalysis = useCallback(async (data) => {
    setAiLoading(true);
    try {
      const indicators = INDICATORS.map(({ key, label, thresholds, invert }) => {
        const v = data[key]?.value;
        const level = getRiskLevel(key, v, thresholds, invert);
        const labels = ["Low", "Moderate", "Elevated", "Extreme"];
        return { label, value: v, riskLabel: labels[level] };
      });
      const prob = computeCrashProb(data);
      const res = await fetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ indicators, crashProb: prob }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setAiAnalysis(json.analysis || "Analysis unavailable.");
    } catch (e) {
      setAiAnalysis("Analysis unavailable: " + e.message);
    }
    setAiLoading(false);
  }, []);

  useEffect(() => {
    fetchLiveData();
    fetchNewsAndRisk();
  }, [fetchLiveData, fetchNewsAndRisk]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loading && lastUpdated) fetchAiAnalysis(liveData); }, [loading]);

  const handleRefresh = () => { fetchLiveData(); fetchNewsAndRisk(); };

  const crashProb = computeCrashProb(liveData);
  const probColor = crashProb < 20 ? "#22c55e" : crashProb < 30 ? "#f97316" : "#ef4444";
  const { x: nx, y: ny } = needleCoords(crashProb);
  const fmt = (v, unit, invert) => {
    if (v === null || v === undefined) return "Loading…";
    const sign = invert && v > 0 ? "+" : "";
    return `${sign}${v}${unit}`;
  };
  const categoryColors = { Geopolitical: "#ef4444", Trade: "#f97316", Fed: "#3b82f6", Credit: "#eab308", Earnings: "#22c55e" };

  return (
    <div style={{ minHeight: "100vh", background: "#080b10", color: "#e2e8f0", fontFamily: "'IBM Plex Mono', 'Courier New', monospace", padding: "20px 16px", maxWidth: 480, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: "0.6rem", letterSpacing: "3px", color: "#334155", textTransform: "uppercase", marginBottom: 4 }}>LIVE · MARKET INTELLIGENCE</div>
        <h1 style={{ fontSize: "1.2rem", fontWeight: 700, color: "#f1f5f9", lineHeight: 1.2, margin: 0 }}>Stock Market<br />Crash Risk Monitor</h1>
        <div style={{ fontSize: "0.6rem", color: "#334155", marginTop: 4 }}>
          {lastUpdated ? `Last refreshed: ${lastUpdated.toLocaleTimeString()}` : "Fetching live data…"}
          {" · "}<span onClick={handleRefresh} style={{ color: "#3b82f6", cursor: "pointer", textDecoration: "underline" }}>Refresh</span>
        </div>
        {apiError && <div style={{ fontSize: "0.55rem", color: "#ef4444", marginTop: 4, background: "#450a0a", padding: "4px 8px", borderRadius: 6 }}>{apiError}</div>}
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2330", borderRadius: 14, padding: "20px 16px 14px", marginBottom: 14, textAlign: "center" }}>
        <div style={{ fontSize: "0.6rem", letterSpacing: "2px", color: "#475569", textTransform: "uppercase", marginBottom: 10 }}>Estimated Crash Probability (20%+ Decline)</div>
        <svg viewBox="0 0 220 130" style={{ width: "100%", maxWidth: 260, overflow: "visible" }}>
          <defs><linearGradient id="arcG" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#22c55e" /><stop offset="45%" stopColor="#eab308" /><stop offset="75%" stopColor="#f97316" /><stop offset="100%" stopColor="#ef4444" /></linearGradient></defs>
          <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke="#1a2035" strokeWidth="16" strokeLinecap="round" />
          <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke="url(#arcG)" strokeWidth="16" strokeLinecap="round" opacity="0.9" />
          <line x1="110" y1="110" x2={nx} y2={ny} stroke={probColor} strokeWidth="3" strokeLinecap="round" />
          <circle cx="110" cy="110" r="6" fill={probColor} />
          <text x="20" y="128" fill="#334155" fontSize="9" textAnchor="middle">0%</text>
          <text x="110" y="24" fill="#334155" fontSize="9" textAnchor="middle">50%</text>
          <text x="200" y="128" fill="#334155" fontSize="9" textAnchor="end">100%</text>
        </svg>
        <div style={{ fontSize: "3rem", fontWeight: 900, color: probColor, lineHeight: 1, marginTop: -10 }}>{loading ? "…" : `${crashProb}%`}</div>
        <div style={{ fontSize: "0.58rem", color: "#475569", marginTop: 4 }}>Blended estimate · Published baseline: ~26% (daytrading.com)</div>
        <div style={{ fontSize: "0.52rem", color: "#334155", marginTop: 2 }}>Model-based. Not a published forecast. For reference only.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        {[
          { label: "No crash", pct: Math.max(0, 100 - crashProb - Math.round((100 - crashProb) * 0.29)), color: "#22c55e", bg: "#052e16" },
          { label: "Bear mkt", pct: Math.round((100 - crashProb) * 0.29), color: "#eab308", bg: "#422006" },
          { label: "Crash", pct: crashProb, color: "#ef4444", bg: "#450a0a" }
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}22`, borderRadius: 10, padding: "10px 6px", textAlign: "center" }}>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color: s.color }}>{s.pct}%</div>
            <div style={{ fontSize: "0.58rem", color: "#64748b", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
        {["indicators", "news"].map(tab => (
          <div key={tab} onClick={() => setActiveTab(tab)} style={{ padding: "8px", textAlign: "center", borderRadius: 8, cursor: "pointer", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", background: activeTab === tab ? "#1e2a3a" : "#0d1117", color: activeTab === tab ? "#93c5fd" : "#334155", border: activeTab === tab ? "1px solid #3b82f655" : "1px solid #1e2330", transition: "all 0.2s" }}>
            {tab === "indicators" ? "📊 Indicators" : "📰 News Risk"}
          </div>
        ))}
      </div>

      {activeTab === "indicators" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {INDICATORS.map(({ key, label, description, thresholds, unit, riskLabels, invert }) => {
            const val = liveData[key]?.value;
            const src = liveData[key]?.source;
            const upd = liveData[key]?.updated;
            const level = getRiskLevel(key, val, thresholds, invert);
            const pct = getBarPct(key, val, thresholds, invert);
            const color = RISK_COLORS[level];
            const bg = RISK_BG[level];
            return (
              <div key={key} style={{ background: "#0d1117", border: `1px solid ${color}22`, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#cbd5e1" }}>{label}</div>
                    <div style={{ fontSize: "0.58rem", color: "#475569", marginTop: 1 }}>{description}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color }}>{loading ? "…" : fmt(val, unit, invert)}</div>
                    <div style={{ fontSize: "0.55rem", background: bg, color, padding: "1px 6px", borderRadius: 6, marginTop: 2, display: "inline-block" }}>{riskLabels[level]}</div>
                  </div>
                </div>
                <div style={{ background: "#080b10", borderRadius: 4, height: 5, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.8s ease" }} />
                </div>
                <div style={{ fontSize: "0.52rem", color: "#334155", marginTop: 3 }}>Source: {src} · {upd}</div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "news" && (
        <div style={{ marginBottom: 14 }}>
          {newsRiskScore !== null && (
            <div style={{ background: "#0d1117", border: `1px solid ${NEWS_RISK_COLORS[newsRiskScore]}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ textAlign: "center", flexShrink: 0 }}>
                <div style={{ fontSize: "1.8rem", lineHeight: 1 }}>{["🟢", "🟡", "🟠", "🔴"][newsRiskScore]}</div>
                <div style={{ fontSize: "0.55rem", color: NEWS_RISK_COLORS[newsRiskScore], marginTop: 2 }}>{NEWS_RISK_LABELS[newsRiskScore]}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.6rem", letterSpacing: "1px", color: "#475569", textTransform: "uppercase", marginBottom: 4 }}>News Risk Assessment</div>
                <div style={{ fontSize: "0.68rem", color: "#94a3b8", lineHeight: 1.5 }}>{newsLoading ? "Scanning latest headlines…" : newsRiskSummary}</div>
              </div>
            </div>
          )}
          {newsLoading ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#334155", fontSize: "0.7rem" }}>Fetching & analyzing latest news…</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {newsItems.map((item, i) => (
                <div key={i} style={{ background: "#0d1117", border: `1px solid ${RISK_COLORS[item.risk] || "#1e2330"}22`, borderLeft: `3px solid ${RISK_COLORS[item.risk] || "#334155"}`, borderRadius: "0 10px 10px 0", padding: "10px 12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontSize: "0.68rem", color: "#cbd5e1", lineHeight: 1.4, flex: 1 }}>{item.title}</div>
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                      <div style={{ fontSize: "0.55rem", background: (categoryColors[item.category] || "#475569") + "22", color: categoryColors[item.category] || "#475569", padding: "2px 6px", borderRadius: 6, marginBottom: 3 }}>{item.category}</div>
                      <div style={{ fontSize: "0.55rem", color: RISK_COLORS[item.risk] }}>{["Low", "Mod", "High", "Severe"][item.risk]} risk</div>
                    </div>
                  </div>
                </div>
              ))}
              {newsItems.length === 0 && <div style={{ textAlign: "center", padding: "20px 0", color: "#334155", fontSize: "0.7rem" }}>No headlines loaded. Tap Refresh to try again.</div>}
              <div style={{ fontSize: "0.52rem", color: "#1e2330", textAlign: "right", marginTop: 2 }}>Headlines sourced from NewsAPI · Scored by Claude AI</div>
            </div>
          )}
        </div>
      )}

      <div style={{ background: "#0d0f1a", border: "1px solid #1e2a4a", borderRadius: 12, padding: "14px", marginBottom: 14 }}>
        <div style={{ fontSize: "0.58rem", letterSpacing: "2px", color: "#3b82f6", textTransform: "uppercase", marginBottom: 8 }}>⚡ AI Analysis</div>
        {aiLoading ? <div style={{ fontSize: "0.7rem", color: "#334155" }}>Generating analysis…</div> : <div style={{ fontSize: "0.72rem", color: "#93c5fd", lineHeight: 1.6 }}>{aiAnalysis || "Awaiting data…"}</div>}
        <div style={{ fontSize: "0.55rem", color: "#1e3a5f", marginTop: 8 }}>Powered by Claude · Updates on refresh</div>
      </div>

      <div style={{ fontSize: "0.55rem", color: "#1e2330", textAlign: "center", lineHeight: 1.5 }}>
        Not financial advice. Probability is a blended model estimate anchored to published research. Not a guarantee of future performance. Always consult a qualified financial advisor.
      </div>
    </div>
  );
}
